import * as React from "react"
import { useLocation } from "react-router-dom"
import { useMutation, useQuery } from "@tanstack/react-query"
import { ChevronDown, Loader2, Send, Sparkles } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { useAuth } from "@/lib/auth"
import { t } from "@/lib/i18n"
import { cn } from "@/lib/utils"

import { estadoDeMarty, preguntarAMarty } from "@/lib/martyApi"
import type { RespuestaDeMarty } from "@/lib/martyApi"

/**
 * MARTY, ABAJO A LA IZQUIERDA.
 *
 * Cesar, punto 12 de la cuarta tanda: «deberías de tener a Marty como un pop up
 * abajo a la izquierda para que le puedas preguntar cosas relacionadas a los
 * resultados, meses etc. Y debería de ser en tiempo real». Y a mitad de la
 * construcción: «no sólo [eso], deberíamos de poder preguntarle acerca de todo,
 * ventas, forecast, impuestos, etc. cosas por revisar etc».
 *
 * ===========================================================================
 * LO QUE LO HACE DE TIEMPO REAL NO ES LA ANIMACIÓN: ES EL CONTEXTO
 * ===========================================================================
 *
 * Cada pregunta arma el contexto en el momento, desde la base, con las mismas
 * funciones que pintan las pantallas: el P&L, el corte, la caja, los tres
 * escenarios del forecast, lo declarado con su cobertura firmada, los impuestos
 * pagados y los proyectados, las ventas por cliente, la cartera, lo que hay por
 * revisar y las obligaciones del mes. Ver `app/modules/marty/router.py`.
 *
 * Un índice de ayuda con respuestas escritas contestaría igual de rápido y
 * diría lo mismo en enero que en diciembre.
 *
 * ---------------------------------------------------------------------------
 * LA PANTALLA QUE SE ESTÁ MIRANDO VIAJA CON LA PREGUNTA
 *
 * Es lo que hace que «¿y esto?» tenga sentido. No cambia qué datos se cargan
 * —se cargan todos— pero sí le dice a Marty qué tiene delante quien pregunta.
 *
 * ---------------------------------------------------------------------------
 * LA CERTEZA Y LO QUE LE FALTA SE PINTAN APARTE, NO DENTRO DEL TEXTO
 *
 * El backend los devuelve como campos propios a propósito. Dentro del párrafo
 * se leen como modestia; encima, como lo que son: una advertencia sobre la
 * respuesta. Un asistente financiero que suena igual de seguro cuando sabe y
 * cuando no es peor que no tenerlo.
 *
 * ---------------------------------------------------------------------------
 * CERRADO POR DEFECTO, Y CUESTA CRÉDITOS
 *
 * Es un botón hasta que alguien lo abre, y no pregunta nada por su cuenta: cada
 * respuesta es una llamada al modelo de razonamiento. Nada de «¿quieres que
 * analice esto?» al entrar en una pantalla.
 */
export function MartyFlotante() {
  const { activeTenantId } = useAuth()
  const { pathname } = useLocation()
  const [abierto, setAbierto] = React.useState(false)
  const [pregunta, setPregunta] = React.useState("")
  const [respuesta, setRespuesta] = React.useState<RespuestaDeMarty | null>(null)

  const estado = useQuery({
    queryKey: ["marty-estado", activeTenantId],
    queryFn: estadoDeMarty,
    enabled: Boolean(activeTenantId) && abierto,
  })

  const preguntar = useMutation({
    mutationFn: () => preguntarAMarty({ pregunta: pregunta.trim(), pantalla: pathname }),
    onSuccess: (r) => {
      setRespuesta(r)
      setPregunta("")
    },
  })

  if (!activeTenantId) return null

  if (!abierto) {
    return (
      <button
        data-guia="marty"
        type="button"
        onClick={() => setAbierto(true)}
        aria-label={t("Preguntarle a Marty")}
        /*
          A LA DERECHA DEL RIEL, NO ENCIMA. Con `left-4` quedaba sobre la barra
          lateral y tapaba el pie —el correo de quien ha entrado y sus
          capacidades—, que se vio en la captura y no en ningún test: ninguno
          mira si un elemento flotante cubre otro.

          `left-[calc(theme(width.rail)+1rem)]` lo deja pegado al riel por fuera.
          En móvil el riel no está, así que vuelve a `left-4`.
        */
        className={cn(
          "fixed bottom-4 left-4 z-40 flex items-center gap-1.5 rounded-full",
          "md:left-[calc(13.5rem+1rem)]",
          "border bg-surface px-3 py-2 text-xs font-medium shadow-lg",
          "hover:bg-row-hover focus-visible:outline-none focus-visible:ring-2",
          "focus-visible:ring-ring",
        )}
      >
        <Sparkles className="h-3.5 w-3.5 text-brand" aria-hidden="true" />
        {t("Pregúntame")}
      </button>
    )
  }

  const noDisponible = estado.data && !estado.data.disponible

  return (
    <aside
      role="dialog"
      aria-label={t("Preguntarle a Marty")}
      className={cn(
        "fixed bottom-4 left-4 z-40 flex w-[min(26rem,calc(100vw-2rem))] flex-col",
        "md:left-[calc(13.5rem+1rem)]",
        "rounded-panel border bg-surface shadow-lg",
      )}
    >
      <header className="flex items-center gap-2 border-b px-3 py-2">
        <Sparkles className="h-4 w-4 shrink-0 text-brand" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium leading-tight">{t("Pregúntame")}</p>
          <p className="text-2xs text-muted-foreground">
            {t("Sobre tus cifras: ventas, resultados, caja, forecast, impuestos o qué te falta revisar.")}
          </p>
        </div>
        <Button size="iconSm" variant="ghost" aria-label={t("Cerrar")}
                onClick={() => setAbierto(false)}>
          <ChevronDown />
        </Button>
      </header>

      <div className="max-h-[22rem] space-y-2 overflow-y-auto px-3 py-2.5">
        {noDisponible ? (
          <p className="text-2xs leading-5 text-warning">{estado.data?.motivo}</p>
        ) : null}

        {respuesta ? (
          <div className="space-y-1.5">
            <p className="whitespace-pre-wrap text-xs leading-5">{respuesta.respuesta}</p>
            <div className="flex flex-wrap items-center gap-1.5">
              {/*
                LA CERTEZA, SIEMPRE A LA VISTA. Una respuesta de certeza baja
                leída como una de certeza alta es el único modo en que esta
                pieza puede hacer daño.
              */}
              <Badge
                variant={respuesta.certeza === "alta" ? "success-soft"
                  : respuesta.certeza === "baja" ? "warning-soft" : "outline"}
                estado
              >
                {t("certeza")} {respuesta.certeza}
              </Badge>
              <span className="code text-2xs text-muted-foreground">
                {respuesta.modelo}
              </span>
              {/*
                SI EL PROVEEDOR PRINCIPAL FALLÓ, SE DICE. La respuesta es buena
                igual —la cadena degradó y contestó el siguiente— pero que el de
                cabecera esté caído es algo que alguien tiene que saber antes de
                que caiga también el segundo. La primera prueba real degradó por
                «sin créditos».
              */}
              {(respuesta.degradaciones ?? []).map((d) => (
                <Badge key={d.proveedor} variant="warning-soft" estado>
                  {d.proveedor}: {d.causa}
                </Badge>
              ))}
            </div>
            {respuesta.que_me_falta ? (
              <p className="rounded-md border border-dashed px-2 py-1.5 text-2xs leading-5 text-muted-foreground">
                <strong className="font-medium">{t("Lo que me falta:")}</strong>{" "}
                {respuesta.que_me_falta}
              </p>
            ) : null}
          </div>
        ) : null}

        {preguntar.isError ? (
          <p className="text-2xs leading-5 text-destructive">
            {preguntar.error instanceof Error
              ? preguntar.error.message
              : t("No pude contestarte.")}
          </p>
        ) : null}

        {!respuesta && !preguntar.isPending && !noDisponible ? (
          <ul className="space-y-1 text-2xs leading-5 text-muted-foreground">
            <li className="font-medium text-foreground">{t("Por ejemplo:")}</li>
            {[
              "¿Cómo van las ventas contra el año pasado?",
              "¿Cuánto me queda por pagar de impuestos este año?",
              "¿Qué parte de mi forecast está respaldada por contratos firmados?",
              "¿Qué tengo pendiente de revisar?",
            ].map((ej) => (
              <li key={ej}>
                <button
                  type="button"
                  className="text-left underline-offset-2 hover:text-foreground hover:underline"
                  onClick={() => setPregunta(ej)}
                >
                  {t(ej)}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <footer className="space-y-1.5 border-t px-3 py-2.5">
        <Textarea
          rows={2}
          value={pregunta}
          disabled={Boolean(noDisponible) || preguntar.isPending}
          onChange={(e) => setPregunta(e.target.value)}
          onKeyDown={(e) => {
            // Enter manda, Mayús+Enter salta de línea: es lo que hace todo el
            // mundo sin pensarlo, y una caja que no lo hace se siente rota.
            if (e.key === "Enter" && !e.shiftKey && pregunta.trim().length >= 3) {
              e.preventDefault()
              preguntar.mutate()
            }
          }}
          placeholder={t("Escríbeme lo que quieras saber de tus números…")}
          className="text-xs"
        />
        <div className="flex items-center justify-between gap-2">
          <span className="text-2xs text-muted-foreground">
            {t("Contesto con tus datos de ahora, no con un manual.")}
          </span>
          <Button
            size="xs"
            disabled={pregunta.trim().length < 3 || preguntar.isPending || Boolean(noDisponible)}
            onClick={() => preguntar.mutate()}
          >
            {preguntar.isPending ? <Loader2 className="animate-spin" /> : <Send />}
            {preguntar.isPending ? t("Mirando tus cifras…") : t("Preguntar")}
          </Button>
        </div>
      </footer>
    </aside>
  )
}
